import { computed, type MaybeRefOrGetter } from 'vue'

import dayjs from 'dayjs'

import { useGroups } from '@communities/groups-layer/composables/useGroups'

import { transformEventInput } from '../helpers'
import { useEventsQueryStore } from '../stores/events.query-store'
import { useEventsUIStore } from '../stores/events.ui-store'

import {
  CalendarViewTypes,
  type EventFormDataType,
  type EventSearchResult,
  type MeetingType,
  type OpenEventMembership,
} from '../types/events'

export const useEvents = (groupId?: MaybeRefOrGetter<string | undefined>) => {
  // If no groupId provided, get it from the current groups context
  if (!groupId) {
    const { store: groupStore } = useGroups()
    const currentGroupId = computed(() => groupStore.currentGroup?._id)

    if (!currentGroupId.value) {
      console.warn('[useEvents] No groupId provided and no current group found')
    }

    groupId = currentGroupId
  }

  const queryStore = useEventsQueryStore(groupId)

  const uiStore = useEventsUIStore()

  /**
   * Location visibility for one specific event. Pass `isGroupAdmin` from the calling
   * surface (`useGroupPageContext`) — this composable does not load membership.
   */
  const canViewLocationFor = (event?: EventSearchResult | null, isGroupAdmin = false) => {
    if (isGroupAdmin) return true
    if (!event?.eventLocation?.hideEventLocationFromNonAttendees) return true

    return Boolean(event.rsvpMeta?.hasRegistered)
  }

  /** Location visibility for the event currently open in the detail modal. */
  const canViewSelectedEventLocation = (isGroupAdmin = false) => canViewLocationFor(uiStore.selectedEvent, isGroupAdmin)

  /**
   * Selects an event for the detail modal. Calendar/list openers pass membership
   * from the in-tree surface that is opening the event.
   * Plan coverage (`isPartOfPlan`) comes on the events list payload.
   */
  const openEvent = (event: EventSearchResult | null, membership?: OpenEventMembership) => {
    if (!event) {
      uiStore.setSelectedEvent(null)
      return
    }

    if (membership && !membership.isActiveMember) {
      membership.onJoinRequired()
      return
    }

    uiStore.setSelectedEvent(event)
  }

  // Factory functions for queries - callers invoke these when needed
  const createSearchEventsQuery = (searchText: MaybeRefOrGetter<string>) => {
    return queryStore.searchEventsQuery(searchText)
  }

  const createEventsListQuery = () => {
    return queryStore.eventsListQuery()
  }

  const createPastAndUpcomingEventsQuery = () => {
    return queryStore.pastAndUpcomingEventsQuery()
  }

  const getCalendarEvents = (startDate: MaybeRefOrGetter<Date>, endDate: MaybeRefOrGetter<Date>) => {
    return queryStore.calendarEventsQuery(startDate, endDate)
  }

  /**
   * Get a single event by id. Backend resolves the next recurring occurrence;
   * display should use `nextOccurrence || startTime` from the response.
   *
   * @param eventId - event id from mention `data-id`
   * @param options - `enabled` to gate auto-fetch
   */
  const getEventById = (eventId: MaybeRefOrGetter<string>, options?: { enabled?: MaybeRefOrGetter<boolean> }) => {
    return queryStore.fetchEventByIdQuery(eventId, options)
  }

  // Get event RSVP count by ID (accepts reactive values)
  const getEventRSVPCount = (eventId: MaybeRefOrGetter<string>) => {
    return queryStore.eventCountQuery(eventId)
  }

  // Get event attendees list by ID (accepts reactive values)
  const getEventAttendees = (eventId: MaybeRefOrGetter<string>) => {
    return queryStore.getAttendeesQuery(eventId)
  }

  async function joinEvent(eventId: string, paymentMeta?: { paymentStatus: string }) {
    return queryStore.updateRSVPMutation.mutateAsync({
      eventId,
      status: 'ATTENDING',
      paymentMeta: paymentMeta,
    })
  }

  const createEvent = async (formData: EventFormDataType) => {
    const transformedFormData = transformEventInput(formData, false)
    return queryStore.createEventMutation.mutateAsync(transformedFormData as any)
  }

  const updateEvent = async (formData: EventFormDataType) => {
    const transformedFormData = transformEventInput(formData, true)
    return queryStore.updateEventMutation.mutateAsync(transformedFormData as any)
  }

  // Export event attendees (manual trigger)
  const exportEventAttendees = (eventId: MaybeRefOrGetter<string>) => {
    return queryStore.exportAttendeesQuery(eventId)
  }

  /**
   * Get delete parameters for recurring event deletion
   * @param event - The event object from FullCalendar
   * @param deleteEventType - 'this' | 'following' | 'all'
   * @returns Delete parameters object with startTime, endTime, and deletion flags
   */
  const getDeleteParams = (event: any, deleteEventType: 'this' | 'following' | 'all') => {
    // Get the original UTC times from the API data stored in extendedProps
    if (!Boolean(event?.isRecurring)) {
      return {}
    }
    const deleteParams: any = {
      startTime: event?.startTime,
      endTime: event?.endTime,
    }

    // If original times are not available in extendedProps, then parse from the event object
    if (!deleteParams.startTime || !deleteParams.endTime) {
      const eventTimezone = event?.timezone || 'UTC'

      // Convert the dates to UTC using dayjs
      const startDayjs = dayjs.tz(event?.start, eventTimezone)
      const endDayjs = dayjs.tz(event?.end, eventTimezone)

      deleteParams.startTime = startDayjs.utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
      deleteParams.endTime = endDayjs.utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
    }

    // Add deletion flags based on type
    if (deleteEventType === 'following') {
      deleteParams.deleteCurrentAndAllFutureOccurrences = true
    } else if (deleteEventType === 'all') {
      deleteParams.deleteAllOccurrences = true
    }

    return deleteParams
  }

  const deleteEvent = async (eventId: string, deleteParams?: any) => {
    return queryStore.deleteEventMutation.mutateAsync({
      eventId,
      deleteParams: deleteParams || {},
    })
  }

  const addEventToCalendar = async (eventId: string, calendar: string) => {
    return queryStore.addEventToCalendarMutation.mutateAsync({
      eventId,
      calendar,
    })
  }

  const createMeeting = (variables: { title: string; type: MeetingType }) => {
    return queryStore.createMeetingMutation.mutateAsync(variables)
  }

  const toggleCreateEventModal = (startDate?: Date) => {
    uiStore.toggleCreateEventModal(startDate)
  }

  const navigateToCalendar = (to: 'next' | 'prev' | 'today') => {
    if (uiStore.calendarRef) {
      if (to === 'next') {
        uiStore.calendarRef.getApi().next()
      } else if (to === 'prev') {
        uiStore.calendarRef.getApi().prev()
      } else if (to === 'today') {
        uiStore.calendarRef.getApi().today()
      }
    }
  }

  const switchCalendarView = (view: 'month' | 'week') => {
    uiStore.setCalendarView(view)
    if (!uiStore.calendarRef) {
      return
    }
    if (view === CalendarViewTypes.WEEK) {
      uiStore.calendarRef.getApi().changeView('timeGridWeek')
    } else if (view === CalendarViewTypes.MONTH) {
      uiStore.calendarRef.getApi().changeView('dayGridMonth')
    }
  }

  return {
    // Query factory functions - callers invoke these when needed
    createSearchEventsQuery,
    createEventsListQuery,
    createPastAndUpcomingEventsQuery,
    getCalendarEvents,

    // Single event detail (for popovers / inline embed cards)
    getEventById,

    // Event RSVP count
    getEventRSVPCount,

    // Event attendees
    getEventAttendees,
    exportEventAttendees,

    // Navigate to calendar
    navigateToCalendar,
    switchCalendarView,

    // RSVP mutation
    joinEvent,

    // Create event mutation
    createEvent,
    updateEvent,
    deleteEvent,
    getDeleteParams,

    // Add to calendar
    addEventToCalendar,

    // Create meeting (e.g. Live Room) — exposes mutation for isPending access
    createMeeting,
    createMeetingMutation: queryStore.createMeetingMutation,

    // UI state (individual computed properties)
    selectedEvent: computed(() => uiStore.selectedEvent),
    showCreateEventModal: computed(() => uiStore.showCreateEventModal),
    viewMode: computed(() => uiStore.viewMode),
    calendarView: computed(() => uiStore.calendarView),
    calendarRef: computed(() => uiStore.calendarRef),
    sidebarCalendarDate: computed(() => uiStore.sidebarCalendarDate),
    eventDateTitle: computed(() => uiStore.eventDateTitle),
    eventModalMode: computed(() => uiStore.eventModalMode),

    // Location visibility (the event's hide-from-non-attendees toggle)
    canViewLocationFor,
    canViewSelectedEventLocation,

    // UI actions
    toggleCreateEventModal,
    setSelectedEvent: uiStore.setSelectedEvent,
    openEvent,
    setCalendarView: uiStore.setCalendarView,
    setCalendarRef: uiStore.setCalendarRef,
    setSidebarCalendarDate: uiStore.setSidebarCalendarDate,
    setEventDateTitle: uiStore.setEventDateTitle,
    setEventModalMode: uiStore.setEventModalMode,
    setViewMode: uiStore.setViewMode,

    // Query store (for advanced usage - access mutation states)
    queryStore,
  }
}
